import React from 'react';
import { withStyles } from '@material-ui/core';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Snackbar, Tab, Tabs, Typography, Alert, TextField, InputAdornment, IconButton } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import styles from './lms-course-review.style';
import superAdminTheme from '../../shared/components/data-table/superadmin-theme';
import DataTable from '../../shared/components/data-table/DataTable';
import StateBlock from '../../shared/components/data-table/StateBlock';
import useLmsCourseReviewPageHook from './useLmsCourseReviewPageHook';
import ResolutionDialog from './components/resolution-dialog/resolution-dialog';
import BulkActionsBar from './components/bulk-actions-bar/bulk-actions-bar';
import BulkLinkConfirmModal from './components/bulk-link-confirm-modal/bulk-link-confirm-modal';
import { PAGE_SIZE_OPTIONS, QUEUE_VIEW_OPTIONS } from './constants/lms-course-review.constants';

const CONTROL_HEIGHT = 40;

const LmsCourseReview = () => {
  const { queue, resolution } = useLmsCourseReviewPageHook();

  const {
    columns,
    gridData,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    subtitle,
    serverSidePaginationProps,
    refreshQueue,
    toast,
    handleCloseToast,
    selection,
    bulkLink,
    queueView,
    onQueueViewChange,
    isHistoryView,
    searchInput,
    setSearchInput,
    clearSearch,
  } = queue;

  const hasRows = gridData.length > 0;

  const emptyState = isHistoryView ? (
    <StateBlock
      variant="info"
      title="No reviews yet"
      description="Resolved Canvas courses will appear here once you start linking items."
      data-testid="lms-course-review-empty"
    />
  ) : (
    <StateBlock
      variant="empty"
      title="You're all caught up"
      description="There are no Canvas courses waiting for review right now."
      data-testid="lms-course-review-empty"
    />
  );

  return (
    <ThemeProvider theme={superAdminTheme}>
      <Box sx={{ p: { xs: 2, md: '24px 32px' }, height: '100%', width: '100%', backgroundColor: 'common.white', overflow: 'auto' }}>
        <Box mb={2}>
          <Typography variant="h1" sx={{ color: 'text.primary' }}>
            Course Review
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review Canvas courses ingested from Live Events and link them to Orijin courses.
          </Typography>
        </Box>

        <Tabs
          value={queueView}
          onChange={(_event, value) => onQueueViewChange(value)}
          textColor="primary"
          indicatorColor="primary"
          data-testid="lms-course-review-view-tabs"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2, minHeight: 40 }}
        >
          {QUEUE_VIEW_OPTIONS.map((option) => (
            <Tab key={option.key} label={option.label} value={option.key} sx={{ minHeight: 40, fontWeight: 600 }} />
          ))}
        </Tabs>

        <Box sx={{ mb: 2, maxWidth: { md: 440 } }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by course name or Canvas ID"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            data-testid="lms-course-review-search"
            inputProps={{ 'aria-label': 'Search review queue by course name or Canvas ID' }}
            sx={{ '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
                    <ClearRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {error ? (
          <StateBlock
            variant="error"
            title="Failed to load the review queue"
            description="Something went wrong while loading Canvas courses. Please try again."
            onRetry={refreshQueue}
            data-testid="lms-course-review-error"
          />
        ) : (
          <Box data-testid="lms-course-review-grid">
            {!isHistoryView && hasRows && (
              <Box mb={1.5}>
                <BulkActionsBar
                  selectedCount={selection.selectedCount}
                  linkableCount={selection.linkableCount}
                  pageLinkableCount={selection.pageLinkableCount}
                  pageHighConfidenceCount={selection.pageHighConfidenceCount}
                  onBulkLinkSuggestions={selection.onBulkLinkSuggestions}
                  onSelectAllLinkable={selection.selectAllLinkableOnPage}
                  onSelectHighConfidence={selection.selectHighConfidenceOnPage}
                  onClearSelection={selection.clearSelection}
                  loading={selection.bulkLoading}
                />
              </Box>
            )}
            <DataTable
              columns={columns}
              rows={gridData}
              loading={loading}
              title={isHistoryView ? 'Review history' : 'Pending review'}
              subtitle={subtitle}
              onRefresh={refreshQueue}
              paginationMode="server"
              page={page}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              totalCount={totalCount}
              onPageChange={serverSidePaginationProps.onPageChange}
              onPageSizeChange={serverSidePaginationProps.onPageSizeChange}
              emptyState={emptyState}
            />
          </Box>
        )}
      </Box>

      <BulkLinkConfirmModal
        modalRef={bulkLink.modalRef}
        items={bulkLink.items}
        loading={bulkLink.loading}
        onClose={bulkLink.close}
        onConfirm={bulkLink.confirm}
      />

      <ResolutionDialog
        open={resolution.dialog.isOpen}
        item={resolution.dialog.item}
        isPending={resolution.dialog.isPending}
        showSuggestion={resolution.suggestion.show}
        suggestion={resolution.suggestion.data}
        onDismissSuggestion={resolution.suggestion.dismiss}
        onAcceptSuggestion={resolution.suggestion.accept}
        searchInput={resolution.search.input}
        onSearchInputChange={resolution.search.setInput}
        searchOptions={resolution.search.options}
        searchLoading={resolution.search.loading}
        selectedResource={resolution.search.selectedResource}
        onSelectResource={resolution.search.select}
        onClearSelectedResource={resolution.search.clear}
        onLinkSelected={resolution.search.linkSelected}
        createForm={resolution.createForm.values}
        onCreateFormChange={resolution.createForm.setValues}
        onCreateAndLink={resolution.createForm.submit}
        actionLoading={resolution.actionLoading}
        onClose={resolution.dialog.close}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.type} variant="filled" onClose={handleCloseToast} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ThemeProvider>
  );
};

export default withStyles(styles)(LmsCourseReview);
